import { useCallback, useMemo, useState, lazy, Suspense } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Eye, Plus, MoreHorizontal, Code2 } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../common/hooks"
import {
  LightBtn,
  PageHeader,
  PrimaryBtn,
  APIKeyModal,
  ApprovedUrlsModal
} from "../../common/ui"
import { IntegrationsTable } from "../../common/ui/integrations"
import {
  setIsSubmitting,
  setPreviewModal
} from "../../redux/slices/integrations"
import { selectUserDetails, selectUserRoles } from "../../redux/slices/auth"
import { setVisiblePanel } from "../../redux/slices/app"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { RequireRole } from "../../common/validation"
import Dropdown from "../../common/ui/dropdown"
import { checkIsAdmin, checkIsSuperAdmin } from "../../common/utils"
import {
  useFetchIntegrationsQuery,
  useGetIntegrationDetailsQuery,
  useGetStatusQuery,
  useFetchBrandsQuery
} from "../../redux/queries"
import { DropdownItemProps } from "../../types"
import ManageIntegration from "../../common/ui/integrations/manage"

// Lazy load heavy components
const PreviewModal = lazy(() => import("../../common/ui/preview-modal"))

const IntegrationsPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useAppDispatch()
  const userRoles = useAppSelector(selectUserRoles)
  const isAdmin = useMemo(() => checkIsAdmin(userRoles), [userRoles])
  const isSuperAdmin = useMemo(() => checkIsSuperAdmin(userRoles), [userRoles])
  const user = useAppSelector(selectUserDetails)

  const queryParams = useMemo(
    () => ({
      limit: 999,
      ...(isSuperAdmin ? {} : { org_id: user?.org?.id })
    }),
    [isSuperAdmin, user?.org?.id]
  )

  const { data: brands } = useFetchBrandsQuery(queryParams)
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useFetchIntegrationsQuery(queryParams)

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(id ?? "", {
      skip: !id
    })

  // Get the statuses of the integration
  const { data: statusData = [], isLoading: isLoadingStatus } =
    useGetStatusQuery(
      {
        integrationID: integration?.id ?? ""
      },
      {
        skip: !integration?.id
      }
    )

  const [apiModalOpen, setAPIModalOpen] = useState(false)
  const [approvedUrlsModalOpen, setApprovedUrlsModalOpen] = useState(false)

  const handleSelectIntegration = useCallback(
    (id: string) => navigate(`/integrations/${id}`),
    [navigate]
  )

  const clearSelectedIntegration = useCallback(() => {
    navigate("/integrations")
    dispatch(setIsSubmitting(false))
  }, [dispatch, navigate])

  const onClickNew = useCallback(() => {
    dispatch(setVisiblePanel("integration/new"))
  }, [dispatch])

  const onClosePreviewModal = useCallback(() => {
    dispatch(
      setPreviewModal({
        isOpen: false,
        appKey: ""
      })
    )
  }, [dispatch])

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.integrations}>
      <>
        <div className="min-h-full p-6 bg-white">
          <PageHeader title="Integrations">
            {isAdmin ? (
              <>
                <LightBtn
                  className="ml-3 hidden sm:flex"
                  onClick={() => {
                    setApprovedUrlsModalOpen(true)
                  }}
                >
                  Approved URL'S
                </LightBtn>
                <LightBtn
                  className="ml-3 hidden sm:flex"
                  onClick={() => {
                    setAPIModalOpen(true)
                  }}
                >
                  <Eye size={16} className="mr-1" /> API Key
                </LightBtn>
                <PrimaryBtn className="ml-3" onClick={onClickNew}>
                  <Plus size={16} className="mr-1" /> New{" "}
                  <span className="hidden sm:flex pl-1">Integration</span>
                </PrimaryBtn>
                <div className="flex sm:hidden ml-2 py-3 px-3 rounded-md border-black border">
                  <Dropdown
                    items={[
                      {
                        name: "Approved URL'S",
                        onClick: (item: DropdownItemProps) =>
                          setApprovedUrlsModalOpen(true)
                      },
                      {
                        name: "API Key",
                        onClick: (item: DropdownItemProps) =>
                          setAPIModalOpen(true)
                      }
                    ]}
                    menuBtn={
                      <MoreHorizontal size={20} className="outline-none" />
                    }
                  />
                </div>
              </>
            ) : null}
          </PageHeader>

          <IntegrationsTable
            integrations={integrations}
            brands={brands}
            isLoading={isLoadingIntegrations}
            onSelect={handleSelectIntegration}
            emptyStateProps={{
              heading: "Add your first integration",
              subHeading: "",
              icon: Code2,
              isActionButton: true,
              buttonText: "Add new integration",
              onClick: onClickNew
            }}
          />
        </div>
        <APIKeyModal
          show={apiModalOpen}
          toggleModal={() => setAPIModalOpen(false)}
        />
        <ApprovedUrlsModal
          show={approvedUrlsModalOpen}
          toggleModal={setApprovedUrlsModalOpen}
        />
        <Suspense fallback={<div>Loading...</div>}>
          <PreviewModal onClose={onClosePreviewModal} />
        </Suspense>

        <ManageIntegration
          open={!!id}
          isCreate={false}
          integration={integration}
          statusData={statusData}
          isLoadingStatus={isLoadingStatus}
          isLoadingIntegration={isLoadingIntegration}
          onClose={clearSelectedIntegration}
        />
      </>
    </RequireRole>
  )
}

export default IntegrationsPage
